import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Logo } from "@/components/Logo";
import { Turnstile } from "@/components/Turnstile";

const schema = z.object({ email: z.string().email("Enter a valid email") });
type FormData = z.infer<typeof schema>;

export default function AdminForgotPassword() {
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit({ email }: FormData) {
    if (!captchaToken) return;
    // Shares the patient /auth/reset handler; ResetPassword routes admins
    // back through the MFA flow once the new password is set.
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
      captchaToken,
    });
    setSent(true);
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
        <div className="flex justify-center mb-1.5">
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-fh-gray bg-fh-sand rounded-full px-2.5 py-0.5">
            Admin
          </span>
        </div>
        {sent ? (
          <>
            <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
              Check your email
            </h1>
            <p className="text-[14px] text-fh-gray text-center">
              If an admin account exists for that email, we've sent a link to reset your password.
            </p>
            <p className="text-center text-[13px] text-fh-gray mt-6">
              <Link to="/admin/login" className="font-semibold text-fh-green hover:text-fh-green-dark">
                Back to sign in
              </Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
              Reset your password
            </h1>
            <p className="text-[14px] text-fh-gray text-center mb-6">
              Enter your admin email and we'll send a reset link.
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5" noValidate>
              <FormField
                label="Email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                autoFocus
                error={errors.email?.message}
                {...register("email")}
              />
              <Turnstile siteKey={env.VITE_TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
              <Button type="submit" loading={isSubmitting || !captchaToken} fullWidth className="mt-1">
                Send reset link
              </Button>
            </form>
            <p className="text-center text-[13px] text-fh-gray mt-[22px]">
              <Link to="/admin/login" className="font-semibold text-fh-green hover:text-fh-green-dark">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
